import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  MarkerClusterer,
  DirectionsRenderer,
  OverlayView,
  InfoWindow,
} from "@react-google-maps/api";
import {
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  OutlinedInput,
  Box as MuiBox,
} from "@mui/material";
import config from "../../config";
import {
  getDestinationStopIcon as getSchoolDestinationStopIcon,
  getOriginStopIcon as getSchoolOriginStopIcon,
  getIntermediateStopIcon as getSchoolIntermediateStopIcon,
} from "../school/icons";
import {
  calculateBearing,
  calculateDistance,
} from "../../utils/calculateDistance";
import { getRightToLeftBusIcon } from "../../pages/stops/vehicleIcons.js";
import MapLegend from "./MapLegend.jsx";
import { getPlaceConfig } from "./functions.js";
import useConfiguredGoogleMap from "../../hooks/useConfiguredGoogleMap.js";
import { useSettings } from "../../context/SettingsContext";
import { useMapIcons } from "../../hooks/useMapIcons.js";
import { useTranslation } from "react-i18next";

const libraries = ["places", "directions"];
const GOOGLE_MAPS_API_KEY = config.googleMapsApiKey;

// Helper to normalize device data
const normalizeDeviceData = (data) => {
  return {
    deviceId: data.deviceId || data.device_id,
    latitude: data.latitude || data.lat,
    longitude: data.longitude || data.lng,
    timestamp: data.timestamp || Date.now(),
  };
};

// Helper function to decode polyline
function decodePolyline(encoded) {
  const points = [];
  let index = 0;
  const len = encoded.length;
  let lat = 0;
  let lng = 0;

  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += dlat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const dlng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += dlng;

    points.push({ lat: lat / 1e5, lng: lng / 1e5 });
  }
  return points;
}

// Find nearest point on route for snapping
const findNearestPointOnRoute = (position, directions) => {
  if (!position || !directions?.routes?.[0]) return null;
  const route = directions.routes[0];
  let closestPoint = null;
  let minDistance = Infinity;
  let routeIndex = -1;
  let currentRouteIndex = 0;

  route.legs.forEach((leg) => {
    leg.steps.forEach((step) => {
      const path = step.path;
      path.forEach((point, index) => {
        if (index === 0) return;
        const getLat = (p) => (typeof p.lat === "function" ? p.lat() : p.lat);
        const getLng = (p) => (typeof p.lng === "function" ? p.lng() : p.lng);

        const start = {
          lat: getLat(path[index - 1]),
          lng: getLng(path[index - 1]),
        };
        const end = { lat: getLat(point), lng: getLng(point) };

        const dx = end.lng - start.lng;
        const dy = end.lat - start.lat;
        const segmentLength = Math.sqrt(dx * dx + dy * dy);
        if (segmentLength === 0) return;

        const t =
          ((position.lng - start.lng) * dx + (position.lat - start.lat) * dy) /
          (segmentLength * segmentLength);

        if (t < 0) {
          const distToStart = calculateDistance(position, start);
          if (distToStart < minDistance) {
            minDistance = distToStart;
            closestPoint = start;
            routeIndex = currentRouteIndex;
          }
          return;
        }

        if (t > 1) {
          const distToEnd = calculateDistance(position, end);
          if (distToEnd < minDistance) {
            minDistance = distToEnd;
            closestPoint = end;
            routeIndex = currentRouteIndex + 1;
          }
          return;
        }

        const projectedPoint = {
          lat: start.lat + t * dy,
          lng: start.lng + t * dx,
        };
        const distance = calculateDistance(position, projectedPoint);
        if (distance < minDistance) {
          minDistance = distance;
          closestPoint = projectedPoint;
          routeIndex = currentRouteIndex + t;
        }

        currentRouteIndex++;
      });
    });
  });

  const MAX_SNAP_DISTANCE = 50;
  if (minDistance <= MAX_SNAP_DISTANCE) {
    return { ...closestPoint, routeIndex: routeIndex };
  }
  return null;
};

const containerStyle = {
  width: "100%",
  height: "600px",
  borderRadius: "16px",
  border: "1px solid #183059",
};

function SchoolDashboardMap({ stops = [], routes = [], vehicles = [] }) {
  const { settings, loading } = useSettings();
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const { t } = useTranslation();
  const [map, setMap] = useState(null);
  const [directions, setDirections] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleLocations, setVehicleLocations] = useState([]);
  const [snappedPositions, setSnappedPositions] = useState({});
  const [vehicleIds, setVehicleIds] = useState([]);
  const [vehicleStatuses, setVehicleStatuses] = useState({});
  const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [zoom, setZoom] = useState(10);
  const { placeMarkers, getMapOptions } = useConfiguredGoogleMap({
    map,
    isLoaded,
  });
  const {
    getVehicleIcon: getConfiguredVehicleIcon,
    getIntermediateStopIcon: getConfiguredIntermediateStopIcon,
  } = useMapIcons(settings, vehicles, stops, routes);

  const positionHistoryRef = useRef({});
  const lastKnownHeadings = useRef({});
  const lastDataReceivedRef = useRef({});
  const eventSourcesRef = useRef({});
  const isMountedRef = useRef(true);
  const hoveredVehicleIdRef = useRef(null);
  const vehicleDetailsHoverTimeoutRef = useRef(null);
  const vehicleImageCacheRef = useRef({});
  const MIN_MOVEMENT_THRESHOLD = 5;
  const MAX_HISTORY_LENGTH = 5;

  // Extract vehicle IDs
  useEffect(() => {
    const deviceIds = vehicles.map((v) => v.device_id).filter(Boolean);
    setVehicleIds(deviceIds);
  }, [vehicles]);

  const openVehicleDetails = useCallback(
    async (vehicle) => {
      if (!vehicle?.deviceId) return;

      const vehicleData =
        vehicles.find((v) => v?.device_id === vehicle?.deviceId) || {};
      const vehicleRoute = routes.find(
        (route) => route.id === (vehicleData.routeId || vehicleData.route?.id),
      );
      const vehicleStatus =
        vehicleStatuses[vehicle.deviceId] || vehicleData.status || "UNKNOWN";
      let resolvedImageUrl =
        vehicleImageCacheRef.current[vehicleData.device_id] || null;

      if (!resolvedImageUrl) {
        const imageDoc = vehicleData.documents?.find(
          (doc) => doc.name === "VehicleImage",
        );
        const rawPath = imageDoc?.location || null;

        if (rawPath) {
          const imageUrl = rawPath.startsWith("http")
            ? rawPath
            : `${config.baseURL}/upload/image?fileName=${encodeURIComponent(rawPath)}`;

          try {
            const response = await fetch(imageUrl, { credentials: "include" });
            if (response.ok) {
              const blob = await response.blob();
              resolvedImageUrl = URL.createObjectURL(blob);
              vehicleImageCacheRef.current[vehicleData.device_id] = resolvedImageUrl;
            }
          } catch (error) {
            console.error("Error loading vehicle image:", error);
          }
        }
      }

      setSelectedVehicle({
        ...vehicle,
        name: vehicleData.name || vehicleData.VehicleNumber || "Unknown Vehicle",
        speed: vehicle.speed || vehicle.Speed || "0",
        routeName: vehicleRoute?.name || vehicleData.route?.name || "N/A",
        imageUrl: resolvedImageUrl,
        color: vehicleData.vehicle_color || "green",
        status: vehicleStatus,
        driver: vehicleData.driver || null,
      });
    },
    [routes, vehicleStatuses, vehicles],
  );

  useEffect(() => {
    return () => {
      if (vehicleDetailsHoverTimeoutRef.current) {
        clearTimeout(vehicleDetailsHoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!vehicles.length) return;
    const cache = vehicleImageCacheRef.current;

    vehicles.forEach((vehicle) => {
      const deviceId = vehicle.device_id;
      const imageDoc = vehicle.documents?.find(
        (doc) => doc.name === "VehicleImage",
      );
      const rawPath = imageDoc?.location || null;

      if (!rawPath || cache[deviceId]) return;

      const imageUrl = rawPath.startsWith("http")
        ? rawPath
        : `${config.baseURL}/upload/image?fileName=${encodeURIComponent(rawPath)}`;

      fetch(imageUrl, { credentials: "include" })
        .then((res) => {
          if (!res.ok) throw new Error(`Image fetch failed: ${res.status}`);
          return res.blob();
        })
        .then((blob) => {
          if (!cache[deviceId]) {
            cache[deviceId] = URL.createObjectURL(blob);
          }
        })
        .catch(() => {});
    });

    return () => {
      if (!isMountedRef.current) {
        Object.values(vehicleImageCacheRef.current).forEach((url) =>
          URL.revokeObjectURL(url),
        );
        vehicleImageCacheRef.current = {};
      }
    };
  }, [vehicles]);

  // Update selected routes when routes prop changes - keep empty to show all
  useEffect(() => {
    // Don't auto-select routes - let user choose
  }, [routes]);

  const handleRouteChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedRoutes(typeof value === "string" ? value.split(",") : value);
  };

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Fetch initial vehicle locations
  const fetchInitialLocations = useCallback(async () => {
    if (!vehicleIds.length) return;
    try {
      const promises = vehicleIds.map(async (deviceId) => {
        const response = await fetch(
          `${config.baseURL}/location/latest/${deviceId}`,
        );
        const data = await response.json();
        if (data && data.latitude != null && data.longitude != null) {
          const normalizedData = normalizeDeviceData(data);
          const vehicleData = vehicles.find((v) => v.device_id === deviceId);
          const route = routes.find((r) => r.id === vehicleData?.routeId);
          const direction = directions.find(
            (d) => d.routeColor === route?.routeColor,
          );

          let snappedPosition = null;
          if (direction) {
            const position = {
              lat: parseFloat(data.latitude),
              lng: parseFloat(data.longitude),
            };
            snappedPosition = findNearestPointOnRoute(position, direction);
          }

          positionHistoryRef.current[deviceId] = [
            {
              lat: parseFloat(normalizedData.latitude),
              lng: parseFloat(normalizedData.longitude),
              timestamp: Date.now(),
            },
          ];

          const storedData = JSON.parse(localStorage.getItem(deviceId) || "{}");
          const initialHeading = storedData.lastHeading || 0;
          lastKnownHeadings.current[deviceId] = initialHeading;

          return {
            ...normalizedData,
            latitude: snappedPosition
              ? snappedPosition.lat
              : normalizedData.latitude,
            longitude: snappedPosition
              ? snappedPosition.lng
              : normalizedData.longitude,
            heading: initialHeading,
          };
        }
        return null;
      });

      const locations = await Promise.all(promises);
      const validLocations = locations.filter((loc) => loc !== null);
      setVehicleLocations(validLocations);

      const newSnappedPositions = {};
      validLocations.forEach((loc) => {
        const vehicleData = vehicles.find((v) => v.device_id === loc.deviceId);
        const route = routes.find((r) => r.id === vehicleData?.routeId);
        const direction = directions.find(
          (d) => d.routeColor === route?.routeColor,
        );
        if (direction) {
          const position = {
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
          };
          const snapped = findNearestPointOnRoute(position, direction);
          if (snapped) {
            newSnappedPositions[loc.deviceId] = snapped;
          }
        }
      });
      setSnappedPositions(newSnappedPositions);
    } catch (error) {
      console.error("Error fetching initial vehicle locations:", error);
    }
  }, [vehicleIds, vehicles, routes, directions]);

  // Fetch initial locations on mount
  useEffect(() => {
    isMountedRef.current = true;
    if (vehicleIds.length && directions.length > 0) {
      fetchInitialLocations();
    }
    return () => {
      isMountedRef.current = false;
    };
  }, [vehicleIds, directions, fetchInitialLocations]);

  // SSE for real-time vehicle tracking
  useEffect(() => {
    if (!vehicleIds.length || directions.length === 0) return;

    const query = vehicleIds.map((id) => `vehicleId=${id}`).join("&");
    const sseUrl = `${config.baseURL}/redis/stream?${query}`;
    const eventSource = new EventSource(sseUrl);
    eventSourcesRef.current = { main: eventSource };

    const isValidPosition = (pos) =>
      pos &&
      typeof pos.lat === "number" &&
      typeof pos.lng === "number" &&
      !isNaN(pos.lat) &&
      !isNaN(pos.lng);

    eventSource.onmessage = async (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "connected") {
          console.log("SSE connected:", data);
          return;
        }

        const normalizedData = normalizeDeviceData(data);

        if (
          !normalizedData.deviceId ||
          normalizedData.latitude == null ||
          normalizedData.longitude == null
        ) {
          return;
        }

        const lat = parseFloat(normalizedData.latitude);
        const lng = parseFloat(normalizedData.longitude);

        if (isNaN(lat) || isNaN(lng)) {
          return;
        }

        lastDataReceivedRef.current[normalizedData.deviceId] = Date.now();
        setVehicleStatuses((prev) => ({
          ...prev,
          [normalizedData.deviceId]: "ACTIVE",
        }));

        const vehicleData = vehicles.find(
          (v) => v.device_id === normalizedData.deviceId,
        );
        const route = routes.find((r) => r.id === vehicleData?.routeId);
        const direction = directions.find(
          (d) => d.routeColor === route?.routeColor,
        );

        let snappedPosition = null;
        if (direction) {
          const position = { lat, lng };
          snappedPosition = findNearestPointOnRoute(position, direction);
        }

        const newPosition = {
          lat: snappedPosition ? snappedPosition.lat : lat,
          lng: snappedPosition ? snappedPosition.lng : lng,
        };

        if (isValidPosition(newPosition)) {
          if (!positionHistoryRef.current[normalizedData.deviceId]) {
            positionHistoryRef.current[normalizedData.deviceId] = [];
          }

          positionHistoryRef.current[normalizedData.deviceId].push({
            lat: newPosition.lat,
            lng: newPosition.lng,
            timestamp: Date.now(),
          });

          const history = positionHistoryRef.current[normalizedData.deviceId];
          if (history.length > MAX_HISTORY_LENGTH) {
            positionHistoryRef.current[normalizedData.deviceId] =
              history.slice(-MAX_HISTORY_LENGTH);
          }
        }

        // Calculate heading
        let heading = lastKnownHeadings.current[normalizedData.deviceId] || 0;
        const history =
          positionHistoryRef.current[normalizedData.deviceId] || [];

        if (history.length >= 2) {
          const prevPos = history[history.length - 2];
          const currPos = history[history.length - 1];

          if (isValidPosition(prevPos) && isValidPosition(currPos)) {
            const distMoved = calculateDistance(prevPos, currPos);

            if (distMoved != null && distMoved > MIN_MOVEMENT_THRESHOLD) {
              heading = calculateBearing(
                { latitude: prevPos.lat, longitude: prevPos.lng },
                { latitude: currPos.lat, longitude: currPos.lng },
              );
              lastKnownHeadings.current[normalizedData.deviceId] = heading;
            }
          }
        }

        // Persist heading
        const persisted = JSON.parse(
          localStorage.getItem(normalizedData.deviceId) || "{}",
        );
        persisted.lastHeading = heading;
        localStorage.setItem(
          normalizedData.deviceId,
          JSON.stringify(persisted),
        );

        // Update vehicle locations
        setVehicleLocations((prev) => {
          const existingIndex = prev.findIndex(
            (item) => item.deviceId === normalizedData.deviceId,
          );
          const updatedData = {
            ...normalizedData,
            latitude: newPosition.lat,
            longitude: newPosition.lng,
            heading,
          };

          if (existingIndex >= 0) {
            const newLocations = [...prev];
            newLocations[existingIndex] = updatedData;
            return newLocations;
          }
          return [...prev, updatedData];
        });

        setSnappedPositions((prev) => ({
          ...prev,
          [normalizedData.deviceId]: snappedPosition,
        }));
      } catch (error) {
        console.error("Error processing SSE data:", error);
      }
    };

    eventSource.onerror = (error) => {
      console.error("SSE connection error:", error);
      if (eventSourcesRef.current.main) {
        eventSourcesRef.current.main.close();
        delete eventSourcesRef.current.main;
      }
    };

    return () => {
      if (eventSourcesRef.current.main) {
        eventSourcesRef.current.main.close();
        eventSourcesRef.current = {};
      }
    };
  }, [vehicleIds, directions, stops, routes, vehicles]);

  // Status check interval
  useEffect(() => {
    const statusCheckInterval = setInterval(() => {
      const now = Date.now();
      const timeoutThreshold = 5 * 60 * 1000;
      setVehicleStatuses((prev) => {
        const newStatuses = { ...prev };
        vehicleIds.forEach((deviceId) => {
          const lastReceived = lastDataReceivedRef.current[deviceId];
          if (!lastReceived || now - lastReceived > timeoutThreshold) {
            newStatuses[deviceId] = "DEAD";
          }
        });
        return newStatuses;
      });
    }, 180000);
    return () => clearInterval(statusCheckInterval);
  }, [vehicleIds]);

  // Fetch directions
  useEffect(() => {
    if (isLoaded && routes.length > 0 && map) {
      const directionsService = new window.google.maps.DirectionsService();

      const fetchDirections = async () => {
        // console.log("Fetching directions for routes:", routes);

        const directionsPromises = routes.map((route) => {
          return new Promise((resolve) => {
            const routeCoords = route.routeCoordinates || [];

            if (routeCoords.length < 2) {
              console.warn(
                `Route ${route.id} has less than 2 coordinates, skipping`,
              );
              resolve(null);
              return;
            }

            const origin = new window.google.maps.LatLng(
              parseFloat(routeCoords[0].latitude),
              parseFloat(routeCoords[0].longitude),
            );

            const destination = new window.google.maps.LatLng(
              parseFloat(routeCoords[routeCoords.length - 1].latitude),
              parseFloat(routeCoords[routeCoords.length - 1].longitude),
            );

            const waypoints = routeCoords.slice(1, -1).map((coord) => ({
              location: new window.google.maps.LatLng(
                parseFloat(coord.latitude),
                parseFloat(coord.longitude),
              ),
              stopover: coord.isStop || false, 
            }));

            directionsService.route(
              {
                origin,
                destination,
                waypoints,
                optimizeWaypoints: false,
                travelMode: window.google.maps.TravelMode.DRIVING,
              },
              (result, status) => {
                if (status === window.google.maps.DirectionsStatus.OK) {
                  // console.log(
                  //   `✓ Successfully fetched directions for route ${route.id}`,
                  // );
                  resolve({
                    result: { ...result, routeColor: route.routeColor },
                    routeId: route.id,
                  });
                } else {
                  console.error(
                    `✗ Directions request failed for route ${route.id}:`,
                    status,
                  );
                  resolve(null);
                }
              },
            );
          });
        });

        const results = await Promise.all(directionsPromises);
        const validResults = results.filter((r) => r !== null);

        // console.log(
        //   `Loaded ${validResults.length} out of ${routes.length} routes`,
        // );
        setDirections(validResults.map((r) => r.result));

        // Auto-fit map
        if (validResults.length > 0 && map) {
          const bounds = new window.google.maps.LatLngBounds();
          validResults.forEach(({ result }) => {
            result.routes[0].legs.forEach((leg) => {
              leg.steps.forEach((step) => {
                step.path.forEach((point) => {
                  bounds.extend(point);
                });
              });
            });
          });
          map.fitBounds(bounds);

          setTimeout(() => {
            const currentZoom = map.getZoom();
            if (currentZoom > 15) {
              map.setZoom(15);
            }
          }, 100);
        }
      };

      fetchDirections();
    }
  }, [isLoaded, map, routes]);

  const onLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      setZoom(mapInstance.getZoom() || 10);

      const bounds = new window.google.maps.LatLngBounds();

      routes.forEach((route) => {
        if (route.routeCoordinates) {
          route.routeCoordinates.forEach((coord) => {
            bounds.extend({
              lat: parseFloat(coord.latitude),
              lng: parseFloat(coord.longitude),
            });
          });
        }
      });

      stops.forEach((stop) => {
        bounds.extend({
          lat: parseFloat(stop.latitude),
          lng: parseFloat(stop.longitude),
        });
      });

      vehicles.forEach((vehicle) => {
        if (vehicle.latitude && vehicle.longitude) {
          bounds.extend({
            lat: parseFloat(vehicle.latitude),
            lng: parseFloat(vehicle.longitude),
          });
        }
      });

      if (!bounds.isEmpty()) {
        mapInstance.fitBounds(bounds);

        setTimeout(() => {
          const currentZoom = mapInstance.getZoom();
          if (currentZoom > 15) {
            mapInstance.setZoom(15);
          } else if (currentZoom < 10) {
            mapInstance.setZoom(10);
          }
        }, 100);
      }
    },
    [stops, vehicles, routes],
  );

  const vehicleLegendIconUrl = useMemo(() => {
    if (!isLoaded || !window.google?.maps) {
      const svg = getRightToLeftBusIcon("green", 180).trim();
      return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
    }
    const legendDeviceId = vehicles.find((vehicle) => vehicle?.device_id)?.device_id;
    if (legendDeviceId) {
      return getConfiguredVehicleIcon(legendDeviceId, 90)?.url;
    }
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
      getRightToLeftBusIcon("green", 180).trim(),
    )}`;
  }, [getConfiguredVehicleIcon, isLoaded, vehicles]);

  const routeLegendColors = useMemo(
    () => directions.map((direction) => direction.routeColor || "#000000"),
    [directions],
  );

  const stopLegendItems = useMemo(() => {
    if (!isLoaded || !window.google?.maps) {
      return [
        { label: "Origin", color: "#20A8E0" },
        { label: "Stop", color: "#d32f2f" },
        { label: "Destination", color: "#EE3B2B" },
      ];
    }
    return [
      {
        label: "Origin",
        iconUrl: getSchoolOriginStopIcon()?.url,
      },
      {
        label: "Stop",
        iconUrl:
          getConfiguredIntermediateStopIcon()?.url ||
          getSchoolIntermediateStopIcon()?.url,
      },
      {
        label: "Destination",
        iconUrl: getSchoolDestinationStopIcon()?.url,
      },
    ];
  }, [
    getConfiguredIntermediateStopIcon,
    isLoaded,
  ]);

  if (loadError) {
    return <div style={{ padding: "40px" }}>Error loading maps</div>;
  }

  if (loading || !isLoaded) {
    return <div style={{ padding: "40px" }}>Loading map...</div>;
  }

  return (
    <div style={{ padding: "40px", position: "relative" }}>
      {/* Route Filter Dropdown */}
      {routes.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "50px",
            right: "50px",
            zIndex: 10,
            minWidth: "250px",
          }}
        >
          <FormControl
            fullWidth
            size="small"
            sx={{ backgroundColor: "#F2F2F3", borderRadius: "8px" }}
          >
            <Select
              labelId="route-filter-label"
              id="route-filter"
              multiple
              displayEmpty
              value={selectedRoutes}
              onChange={handleRouteChange}
              input={<OutlinedInput />}
              renderValue={(selected) =>
                selected.length === 0 ? (
                  <span style={{ color: "#aaa", fontSize: "14px" }}>
                    Filter by Route
                  </span>
                ) : (
                  `${selected.length} route(s) selected`
                )
              }
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
            >
              {routes.map((route) => (
                <MenuItem key={route.id} value={route.id}>
                  <Checkbox checked={selectedRoutes.indexOf(route.id) > -1} />
                  <MuiBox
                    sx={{
                      width: "20px",
                      height: "4px",
                      backgroundColor: route.routeColor,
                      borderRadius: "2px",
                      marginRight: "8px",
                    }}
                  />
                  <ListItemText primary={route.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}

      <GoogleMap
        mapContainerStyle={containerStyle}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={() => {
          if (map) {
            setZoom(map.getZoom() || 10);
          }
        }}
        options={getMapOptions({
          disableDefaultUI: false,
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: true,
          streetViewControl: true,
          rotateControl: true,
          fullscreenControl: true,
        })}
      >
        {placeMarkers.map((place) => {
          const config = getPlaceConfig(place.type);
          if (!config?.icon) return null;

          return (
            <Marker
              key={place.id}
              position={place.position}
              title={place.name}
              icon={{
                url: config.icon,
                scaledSize: new window.google.maps.Size(32, 32),
              }}
            />
          );
        })}

        {/* Live Status Indicator */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "10px",
            backgroundColor: "white",
            padding: "12px 16px",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: "12px",
              height: "12px",
              borderRadius: "50%",
              backgroundColor: "#ff2f00ff",
              animation: "pulse 2s infinite",
            }}
          />
          <span style={{ fontWeight: "600", fontSize: "14px", color: "#333" }}>
            {t("Live Tracking")}
          </span>
        </div>

        {/* CSS Animation */}
        <style>
          {`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
                transform: scale(1);
              }
              50% {
                opacity: 0.5;
                transform: scale(1.1);
              }
            }
          `}
        </style>

        {/* Render Routes */}
        {directions.map((direction, index) => {
          const route = routes[index];
          const routeId = route?.id;

          // If no routes selected, show all routes
          // If routes selected, only show selected ones
          if (selectedRoutes.length > 0 && !selectedRoutes.includes(routeId))
            return null;

          return (
            <DirectionsRenderer
              key={index}
              directions={direction}
              options={{
                suppressMarkers: true,
                preserveViewport: true,
                polylineOptions: {
                  strokeColor: direction.routeColor || "#4285F4",
                  strokeOpacity: 0.8,
                  strokeWeight: 5,
                },
              }}
            />
          );
        })}

        <MarkerClusterer
          options={(() => {
            const stopIcon =
              getConfiguredIntermediateStopIcon() ||
              getSchoolIntermediateStopIcon();
            const clusterStyle = stopIcon
              ? {
                  url: stopIcon.url,
                  width: 28,
                  height: 28,
                  textColor: "#000000",
                  textSize: 9,
                  fontWeight: "bold",
                  anchorIcon: [28, 14],
                  anchorText: [-1, 0],
                }
              : {
                  url:
                    "data:image/svg+xml;charset=UTF-8," +
                    encodeURIComponent(
                      `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28">
                        <circle cx="14" cy="14" r="12" fill="#008C23" stroke="#fff" stroke-width="2"/>
                      </svg>`,
                    ),
                  width: 28,
                  height: 28,
                  textColor: "#ffffff",
                  textSize: 9,
                  fontWeight: "bold",
                  anchorIcon: [14, 14],
                  anchorText: [-18, 0],
                };
            return {
              styles: [clusterStyle, clusterStyle, clusterStyle],
              gridSize: 31,
              minimumClusterSize: 2,
              zoomOnClick: true,
            };
          })()}
        >
          {(clusterer) =>
            stops.map((stop) => {
              const belongsToSelectedRoute = routes.some((route) => {
                if (selectedRoutes.length === 0) return true;
                if (!selectedRoutes.includes(route.id)) return false;

                return route.routeCoordinates?.some(
                  (coord) =>
                    Math.abs(
                      parseFloat(coord.latitude) - parseFloat(stop.latitude),
                    ) < 0.0001 &&
                    Math.abs(
                      parseFloat(coord.longitude) - parseFloat(stop.longitude),
                    ) < 0.0001,
                );
              });

              if (!belongsToSelectedRoute) return null;

              const lat = parseFloat(stop.latitude);
              const lng = parseFloat(stop.longitude);

              let stopType = "intermediate";
              routes.forEach((route) => {
                if (route.routeCoordinates && route.routeCoordinates.length > 0) {
                  const firstCoord = route.routeCoordinates[0];
                  const lastCoord =
                    route.routeCoordinates[route.routeCoordinates.length - 1];

                  if (
                    Math.abs(parseFloat(firstCoord.latitude) - lat) < 0.0001 &&
                    Math.abs(parseFloat(firstCoord.longitude) - lng) < 0.0001
                  ) {
                    stopType = "origin";
                  } else if (
                    Math.abs(parseFloat(lastCoord.latitude) - lat) < 0.0001 &&
                    Math.abs(parseFloat(lastCoord.longitude) - lng) < 0.0001
                  ) {
                    stopType = "destination";
                  }
                }
              });

              let stopIcon;
              if (stopType === "origin") {
                stopIcon = getSchoolDestinationStopIcon();
              } else if (stopType === "destination") {
                stopIcon = getSchoolOriginStopIcon();
              } else {
                stopIcon =
                  getConfiguredIntermediateStopIcon() ||
                  getSchoolIntermediateStopIcon();
              }

              return (
                <React.Fragment key={stop.id}>
                  {(zoom === null || zoom >= 14) && (
                    <OverlayView
                      position={{ lat, lng }}
                      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                      <div style={{ position: "relative", top: "0px", left: "0px" }}>
                        <div
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            bottom: "44px",
                            backgroundColor: "#FFFFFF",
                            color: "#000000",
                            padding: "3px 8px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "500",
                            boxShadow: "0 2px 4px rgba(0,0,0,0.25)",
                            whiteSpace: "nowrap",
                            zIndex: 1001,
                            pointerEvents: "none",
                          }}
                        >
                          {stop.name || stop.stopId}
                        </div>
                        <svg
                          style={{
                            position: "absolute",
                            left: "50%",
                            transform: "translateX(-50%)",
                            bottom: "40px",
                            pointerEvents: "none",
                            overflow: "visible",
                            zIndex: 1000,
                          }}
                          width="2"
                          height="7"
                        >
                          <line
                            x1="1"
                            y1="0"
                            x2="1"
                            y2="7"
                            stroke="rgba(0,0,0,0.45)"
                            strokeWidth="1.5"
                            strokeDasharray="3 2"
                          />
                        </svg>
                      </div>
                    </OverlayView>
                  )}
                  <Marker
                    position={{ lat, lng }}
                    icon={stopIcon}
                    title={stop.name || stop.stopId}
                    clusterer={clusterer}
                  />
                </React.Fragment>
              );
            })
          }
        </MarkerClusterer>

        {/* Render Live Vehicles */}
        {vehicleLocations.map((vehicle) => {
          const vehicleData =
            vehicles.find((v) => v?.device_id === vehicle?.deviceId) || {};
          const status =
            vehicleStatuses[vehicle.deviceId] || vehicleData.status;

          if (
            selectedRoutes.length > 0 &&
            !selectedRoutes.includes(vehicleData.route?.id)
          )
            return null;

          const vehicleIcon = getConfiguredVehicleIcon(
            vehicle.deviceId,
            vehicle?.heading || 0,
          );

          const vehicleName = vehicleData.name || vehicleData.VehicleNumber;

          return (
            vehicle &&
            vehicle.latitude &&
            vehicle.longitude && (
              <React.Fragment key={vehicle.deviceId}>
                {/* Vehicle Label */}
                <OverlayView
                  position={{
                    lat: parseFloat(vehicle.latitude),
                    lng: parseFloat(vehicle.longitude),
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                >
                  <div
                    style={{
                      position: "relative",
                      top: "13px",
                      left: "0px",
                      width: "2px",
                      height: "2px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "10px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        color: status === "INACTIVE" ? "#000" : "#fff",
                        fontSize: "8px",
                        fontWeight: 600,
                        fontFamily: "Arial, sans-serif",
                        lineHeight: "12px",
                        padding: "6px",
                        textAlign: "left",
                        whiteSpace: "nowrap",
                        backgroundColor:
                          status === "DEAD"
                            ? "#F9E719"
                            : status === "INACTIVE"
                              ? "#F9E719"
                              : "#008C23",
                        borderRadius: "10px",
                        zIndex: 1000,
                        border: "1px solid black",
                      }}
                    >
                      {vehicleName}
                    </div>
                  </div>
                </OverlayView>

                {/* Vehicle Marker */}
                <Marker
                  position={{
                    lat: parseFloat(vehicle.latitude),
                    lng: parseFloat(vehicle.longitude),
                  }}
                  icon={vehicleIcon}
                  onClick={() => openVehicleDetails(vehicle)}
                  onMouseOver={() => {
                    hoveredVehicleIdRef.current = vehicle.deviceId;
                    if (vehicleDetailsHoverTimeoutRef.current) {
                      clearTimeout(vehicleDetailsHoverTimeoutRef.current);
                    }

                    vehicleDetailsHoverTimeoutRef.current = setTimeout(() => {
                      if (hoveredVehicleIdRef.current === vehicle.deviceId) {
                        openVehicleDetails(vehicle);
                      }
                    }, 1000);
                  }}
                  onMouseOut={() => {
                    hoveredVehicleIdRef.current = null;
                    if (vehicleDetailsHoverTimeoutRef.current) {
                      clearTimeout(vehicleDetailsHoverTimeoutRef.current);
                      vehicleDetailsHoverTimeoutRef.current = null;
                    }
                  }}
                />

                {/* Info Window on Click */}
                {selectedVehicle &&
                  selectedVehicle.deviceId === vehicle.deviceId && (
                    <InfoWindow
                      position={{
                        lat: parseFloat(vehicle.latitude),
                        lng: parseFloat(vehicle.longitude),
                      }}
                      onCloseClick={() => setSelectedVehicle(null)}
                      options={{
                        pixelOffset: new window.google.maps.Size(0, -40),
                      }}
                    >
                      <div
                        style={{
                          background: "#E8EAEE",
                          padding: "16px",
                          borderRadius: "12px",
                          minWidth: "auto",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                        }}
                      >
                        <div style={{ fontWeight: "600", marginBottom: "8px" }}>
                          {selectedVehicle.name || vehicleName}
                        </div>
                        <div style={{ fontSize: "12px", color: "#666" }}>
                          Status: {selectedVehicle.status || status || "UNKNOWN"}
                        </div>
                        <div
                          style={{
                            width: "100%",
                            height: "100px",
                            borderRadius: "10px",
                            overflow: "hidden",
                            backgroundColor: "#f5f5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: "12px",
                            marginBottom: "12px",
                          }}
                        >
                          {selectedVehicle.imageUrl ? (
                            <img
                              src={selectedVehicle.imageUrl}
                              alt={selectedVehicle.name || vehicleName}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                              }}
                            />
                          ) : (
                            <img
                              src={`data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
                                getRightToLeftBusIcon(
                                  selectedVehicle.color || "green",
                                  180,
                                ),
                              )}`}
                              alt="vehicle"
                              style={{ width: "80px", height: "80px" }}
                            />
                          )}
                        </div>
                        {vehicleData.driver && (
                          <>
                            <div
                              style={{
                                fontSize: "14px",
                                marginTop: "8px",
                                marginBottom: "4px",
                              }}
                            >
                              Driver: {vehicleData.driver.name}
                            </div>
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {vehicleData.driver.phone}
                            </div>
                          </>
                        )}
                      </div>
                    </InfoWindow>
                  )}
              </React.Fragment>
            )
          );
        })}
        <MapLegend
          showVehicle
          vehicleIconUrl={vehicleLegendIconUrl}
          statusItems={[
            { label: "Active", color: "#008C23", borderColor: "#008C23" },
            { label: "Inactive", color: "#F9E719", borderColor: "#aaa" },
          ]}
          routeColors={routeLegendColors}
          stopItems={stopLegendItems}
        />
      </GoogleMap>
    </div>
  );
}

export default SchoolDashboardMap;
